import { AuthStorage } from "utils/auth-storage";
import {
  Modal,
  Tabs,
  List,
  Tag,
  Card,
  Skeleton,
  Dropdown,
  Select,
  Tooltip,
  TreeSelect,
} from "antd";
import LoadingComponent from "components/loading-component";
import React, { useEffect, useState } from "react";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { Block } from "@material-ui/icons";
import { CheckOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { WarehouseServices } from "apis/Services/WarehouseService";
import toast from "react-hot-toast";
import styles from "./warehouse-dashboard.module.css";
import {
  buildToLocationFilterTreeData,
  matchesToLocationFilter,
} from "./location-filters";

function RequestedTransfersModal({
  requestedTransfers,
  requestedTransferModalVisible,
  setRequestedTransferModalVisible,
  projectID,
  accessLevel,
  transferFilters,
  getTransferRequests,
  isRequestedTransfersLoading,
  getData,
  transfersHistoryData,
  getTransferRequestsCount,
}) {
  const [projectAddressMap, setProjectAddressMap] = useState({});
  const [selectedFromLocationFilter, setSelectedFromLocationFilter] =
    useState(undefined);
  const [selectedToLocationFilter, setSelectedToLocationFilter] =
    useState(undefined);

  // Call data function when modal opens
  useEffect(() => {
    if (requestedTransferModalVisible) {
      getProjectsAddress();
      getTransferRequests();
      getTransferRequestsCount();
    }
  }, [requestedTransferModalVisible]);

  const onCancel = () => {
    setSelectedFromLocationFilter(undefined);
    setSelectedToLocationFilter(undefined);
    setRequestedTransferModalVisible(false);
  };

  function getProjectsAddress() {
    let company_id = AuthStorage.getItem("company_id");
    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");
    let ReqObj = {
      company: company_id,
      code,
      loginEmail: email,
    };
    WarehouseServices.getJobSites(ReqObj)
      .then((res) => {
        if (res) {
          const map = res.reduce((acc, item) => {
            acc[item.name] = item.address;
            return acc;
          }, {});

          setProjectAddressMap(map);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  const fromLocationOptions = (transferFilters?.fromLocation || []).map(
    (location) => ({
      label: location,
      value: location,
    }),
  );

  const toLocationTreeData = buildToLocationFilterTreeData(
    transferFilters?.toLocation,
  );

  const filteredRequestedTransfers = (requestedTransfers || []).filter(
    (transfer) => {
      const fromLocationMatch = selectedFromLocationFilter
        ? transfer.fromLocation === selectedFromLocationFilter
        : true;

      const toLocationMatch = matchesToLocationFilter(
        transfer,
        selectedToLocationFilter,
      );

      return fromLocationMatch && toLocationMatch;
    },
  );

  const filteredTransfersHistoryData = (transfersHistoryData || []).filter(
    (transfer) => {
      const fromLocationMatch = selectedFromLocationFilter
        ? transfer.fromLocation === selectedFromLocationFilter
        : true;

      const toLocationMatch = matchesToLocationFilter(
        transfer,
        selectedToLocationFilter,
      );

      return fromLocationMatch && toLocationMatch;
    },
  );

  const pendingTransfersTab = (
    <div style={{ maxHeight: "800px", overflowY: "auto" }}>
      <List
        dataSource={filteredRequestedTransfers}
        loading={{
          spinning: isRequestedTransfersLoading,
          indicator: <LoadingComponent />,
        }}
        renderItem={(transfer, index) => (
          <Card
            className=" w-100  bg-light bg-opacity-50"
            title={
              <div className="pt-3 pb-1">
                <div className="d-flex flex-column flex-lg-row justify-content-between mb-2 align-items-center gap-3">
                  {/* Left side - Transfer ID and Status */}
                  <div className="d-flex align-items-center  mb-3 mb-lg-0 ">
                    <Tag
                      className="d-flex align-items-center justify-content-center fw-bold"
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                      }}
                      color={statusColors[transfer.approvalStatus]}
                    >
                      <ClockCircleOutlined
                        className="ms-0"
                        style={{
                          // color: statusColors[transfer.approvalStatus],
                          fontSize: "1.3rem",
                        }}
                      />
                    </Tag>

                    <div className="d-flex flex-column justify-content-start gap-0">
                      <div>
                        <h6
                          className="fw-bold m-0 p-0"
                          style={{ fontSize: "0.9rem" }}
                        >
                          Transfer #{transfer.transferID}
                        </h6>
                      </div>
                      <div>
                        <Tag
                          color={
                            statusColors[transfer.approvalStatus] || "default"
                          }
                          bordered={false}
                          className="rounded-5"
                          style={{ fontSize: "0.7rem" }}
                        >
                          {transfer.approvalStatus === "pending"
                            ? "Requested"
                            : transfer.approvalStatus}
                        </Tag>
                      </div>
                    </div>
                  </div>

                  {/* Right side  */}
                  <div className="d-flex flex-column flex-md-row gap-3 gap-md-5 justify-content-start justify-content-md-end align-items-start align-items-md-center">
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#888",
                        fontWeight: "normal",
                      }}
                    >
                      <div className="d-flex flex-column text-md-end">
                        <span className="text-black">
                          <Tooltip title={transfer?.transferOwnerEmail}>
                            {transfer?.fullName}
                          </Tooltip>
                        </span>
                      </div>
                      <div className="d-flex flex-column text-md-end">
                        <span className="fw-bold fs-6 text-black">
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                          }).format(
                            (transfer.items || []).reduce(
                              (sum, item) =>
                                sum + item.unitPrice * item.quantity,
                              0,
                            ),
                          )}
                        </span>
                        <span>{transfer.createdAt}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
            style={{ marginBottom: 16 }}
          >
            <div className=" d-flex justify-content-center align-items-center flex-column">
              <div className=" d-flex w-100 justify-content-md-start align-self-start flex-wrap gap-3 justify-content-start py-0">
                <div>
                  {transfer.items.map((item, index) => (
                    <div key={index}>
                      <div
                        className="d-flex flex-wrap justify-content-start px-1 py-1"
                        style={{ columnGap: "1rem", rowGap: "1rem" }}
                      >
                        {/* Material Info */}
                        <div
                          className="d-md-flex flex-md-row gap-2 align-items-center"
                          style={{ minWidth: "160px" }}
                        >
                          <Inventory2OutlinedIcon
                            style={{ fontSize: "1rem" }}
                            className="opacity-50"
                          />
                          <div className="d-flex flex-column text-start">
                            <small
                              style={{
                                fontSize: "0.85rem",
                                lineHeight: "1.2",
                                fontWeight: "bold",
                              }}
                            >
                              {item.materialName}
                            </small>
                            <small
                              className="opacity-50"
                              style={{
                                fontSize: "0.75rem",
                                lineHeight: "1.2",
                              }}
                            >
                              SKU: {item.sku}
                            </small>
                            {item?.itemVendor ? (
                              <small
                                className="opacity-50"
                                style={{
                                  fontSize: "0.75rem",
                                  lineHeight: "1.2",
                                }}
                              >
                                Vendor: {item.itemVendor}
                              </small>
                            ) : null}
                            <small
                              className="opacity-50"
                              style={{
                                fontSize: "0.75rem",
                                lineHeight: "1.2",
                              }}
                            >
                              {item?.category}
                            </small>
                            <small
                              className="opacity-50"
                              style={{
                                fontSize: "0.75rem",
                                lineHeight: "1.2",
                              }}
                            >
                              {item?.subcategory}
                            </small>
                          </div>
                        </div>

                        {/* Location Info */}
                        <div
                          className="d-md-flex flex-md-row gap-2 align-items-center"
                          style={{ minWidth: "160px" }}
                        >
                          <LocationOnOutlinedIcon
                            style={{ fontSize: "1rem" }}
                            className="opacity-50"
                          />
                          <div className="d-flex flex-column text-start">
                            <small
                              style={{
                                fontSize: "0.85rem",
                                lineHeight: "1.2",
                                fontWeight: "bold",
                              }}
                            >
                              {transfer.toLocation}
                            </small>
                            <small
                              className="opacity-75"
                              style={{
                                fontSize: "0.75rem",
                                lineHeight: "1.2",
                              }}
                            >
                              {Object.keys(projectAddressMap).length > 0 ? (
                                projectAddressMap[transfer.toLocation] ||
                                "No address provided"
                              ) : (
                                <Skeleton.Input
                                  style={{ width: 80, height: 12 }}
                                  active
                                  size="small"
                                />
                              )}
                            </small>
                            <small
                              style={{
                                fontSize: "0.75rem",
                                lineHeight: "1.2",
                              }}
                            >
                              {transfer.toSubLocation}
                            </small>
                          </div>
                        </div>

                        {/* Quantity Info */}
                        <div
                          className="d-flex justify-content-end"
                          style={{ minWidth: "160px" }}
                        >
                          <div className="text-center text-md-end">
                            <small
                              className="fw-bold"
                              style={{
                                fontSize: "0.8rem",
                                lineHeight: "1",
                              }}
                            >
                              Quantity
                            </small>
                            <h5
                              style={{
                                color: "#1777FF",
                                fontWeight: "bold",
                                lineHeight: "1",
                                margin: 0,
                                fontSize: "1.1rem",
                              }}
                            >
                              {item.quantity}{" "}
                              {item.quantity > 1 ? "units" : "unit"}
                            </h5>
                          </div>
                        </div>

                        {/* Amount column */}
                        <div
                          className="d-flex flex-column text-end"
                          style={{ minWidth: "160px" }}
                        >
                          <small
                            className="fw-bold"
                            style={{
                              fontSize: "0.8rem",
                              lineHeight: "1",
                            }}
                          >
                            Amount
                          </small>
                          <h5
                            className=" opacity-50"
                            style={{
                              fontWeight: "bold",
                              lineHeight: "1",
                              margin: 0,
                              fontSize: "1.1rem",
                            }}
                          >
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: "USD",
                            }).format(item.unitPrice * item.quantity)}
                          </h5>
                        </div>
                      </div>

                      {/* Add hr except after the last item */}
                      {index < transfer.items.length - 1 && (
                        <hr className="my-2" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <hr className=" w-100" />
              {transfer.note && (
                <>
                  <div className=" w-100">
                    <strong
                      className=" opacity-75"
                      style={{ fontSize: "0.85rem" }}
                    >
                      Notes
                    </strong>
                    <br />
                    <i style={{ fontSize: "0.8rem" }}>{transfer.note || ""}</i>
                  </div>
                  {/* <hr className=" w-100" /> */}
                </>
              )}
              <div className=" d-flex justify-content-end align-items-center w-100">
                <div>
                  {canUpdate(transfer) && (
                    <div className=" d-flex gap-2">
                      <Dropdown.Button
                        type="primary"
                        disabled={accessLevel < 2}
                        menu={{
                          items,
                          onClick: ({ key }) =>
                            handleUpdateTransfer(transfer, "rejected"),
                        }}
                        onClick={() =>
                          handleUpdateTransfer(transfer, "approved")
                        }
                        style={{ fontSize: "0.8rem" }}
                      >
                        {transfer.approvalStatus === "pending" && (
                          <div className="d-flex align-items-center gap-1">
                            {/* <CheckOutlined /> */}
                            <span style={{ fontSize: "0.85rem" }}>
                              Accept Transfer
                            </span>
                          </div>
                        )}
                      </Dropdown.Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}
      />
    </div>
  );

  const statusColors = {
    Pending: "orange",
    "In Transit": "blue",
    Transit: "blue",
    Delivered: "green",
    approved: "green",
    rejected: "red",
    Cancelled: "red",
    pending: "geekblue",
  };

  const canUpdate = (transfer) => {
    return transfer.approvalStatus === "pending";
  };

  const items = [
    {
      key: "cancel",
      icon: <Block />,
      label: "Reject Transfer",
      danger: true,
      // onClick: () => handleCancelTransfer(transfer),
    },
  ];

  const handleUpdateTransfer = (transfer, approvalStatus) => {
    let company_id = AuthStorage.getItem("company_id");
    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");

    const items = (transfer.items || []).map((item) => ({
      itemID: item.itemID,
      quantity: item.quantity,
    }));

    let ReqObj = {
      company: company_id,
      project: projectID,
      code,
      loginEmail: email,
      values: [
        {
          transferID: transfer.transferID,
          fromLocation: transfer.fromLocation,
          toLocation: transfer.toLocation,
          toLocationID: transfer.toLocationID,
          toSubLocation: transfer.toSubLocation || "",
          note: transfer.note || "",
          approvalStatus: approvalStatus,
          items: items,
        },
      ],
    };

    // console.log(ReqObj, transfer, approvalStatus, "HAAAA???");

    WarehouseServices.approveTransferRequests(ReqObj)
      .then((res) => {
        toast.success(
          approvalStatus === "approved"
            ? "Request has been approved"
            : "Transfer request has been rejected",
        );
        getTransferRequests();
        getTransferRequestsCount();
        getData();
      })
      .catch((err) => {
        console.log(err);
        if (err.status === 404) {
          toast.error(
            "Material item(s) requested for transfer no longer exists.",
          );
        } else if (err.status === 400) {
          toast.error("Requested quantity is unavailable.");
        } else {
          toast.error("Something went wrong, please try again.");
        }
      });
  };

  const historyTab = (
    <div style={{ maxHeight: "800px", overflowY: "auto" }}>
      <List
        dataSource={filteredTransfersHistoryData}
        loading={{
          spinning: isRequestedTransfersLoading,
          indicator: <LoadingComponent />,
        }}
        renderItem={(transfer, index) => (
          <Card
            className=" w-100  bg-light bg-opacity-50"
            title={
              <div className="pt-3 pb-1">
                <div className="d-flex flex-column flex-lg-row justify-content-between mb-2 align-items-center gap-3">
                  {/* Left side - Transfer ID and Status */}
                  <div className="d-flex align-items-center  mb-3 mb-lg-0 ">
                    <Tag
                      className="flex items-center justify-center align-items-center fw-bold"
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      color={statusColors[transfer.approvalStatus] || "default"}
                    >
                      {transfer.approvalStatus === "approved" && (
                        <CheckOutlined
                          style={{
                            color: statusColors[transfer.approvalStatus],
                            fontSize: "1.3rem",
                          }}
                        />
                      )}

                      {transfer.approvalStatus === "rejected" && (
                        <Block
                          style={{
                            color: statusColors[transfer.approvalStatus],
                            fontSize: "1.3rem",
                          }}
                        />
                      )}
                    </Tag>

                    <div className="d-flex flex-column justify-content-start gap-0">
                      <div>
                        <h6
                          className="fw-bold m-0 p-0"
                          style={{ fontSize: "0.9rem" }}
                        >
                          Transfer #{transfer.transferID}
                        </h6>
                      </div>
                      <div>
                        <Tag
                          color={
                            statusColors[transfer.approvalStatus] || "default"
                          }
                          bordered={false}
                          className="rounded-5"
                          style={{ fontSize: "0.7rem" }}
                        >
                          {transfer.approvalStatus === "approved"
                            ? "Approved"
                            : "Rejected"}
                        </Tag>
                      </div>
                    </div>
                  </div>

                  {/* Right side  */}
                  <div className="d-flex flex-column flex-md-row gap-3 gap-md-5 justify-content-start justify-content-md-end align-items-start align-items-md-center">
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#888",
                        fontWeight: "normal",
                      }}
                    >
                      <div className="d-flex flex-column text-md-end">
                        <span className="text-black">
                          <Tooltip title={transfer?.transferOwnerEmail}>
                            {transfer?.fullName}
                          </Tooltip>
                        </span>
                      </div>
                      <div className="d-flex flex-column text-md-end">
                        <span className="fw-bold fs-6 text-black">
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                          }).format(
                            (transfer.items || []).reduce(
                              (sum, item) =>
                                sum + item.unitPrice * item.quantity,
                              0,
                            ),
                          )}
                        </span>
                        <span>{transfer.createdAt}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
            style={{ marginBottom: 16 }}
          >
            <div className=" d-flex justify-content-center align-items-center flex-column">
              <div className=" d-flex w-100 justify-content-md-start align-self-start flex-wrap gap-3 justify-content-start py-0">
                <div>
                  {transfer.items.map((item, index) => (
                    <div key={index}>
                      <div
                        className="d-flex flex-wrap justify-content-start px-1 py-1"
                        style={{ columnGap: "1rem", rowGap: "1rem" }}
                      >
                        {/* Material Info */}
                        <div
                          className="d-md-flex flex-md-row gap-2 align-items-center"
                          style={{ minWidth: "160px" }}
                        >
                          <Inventory2OutlinedIcon
                            style={{ fontSize: "1rem" }}
                            className="opacity-50"
                          />
                          <div className="d-flex flex-column text-start">
                            <small
                              style={{
                                fontSize: "0.85rem",
                                lineHeight: "1.2",
                                fontWeight: "bold",
                              }}
                            >
                              {item.materialName}
                            </small>
                            <small
                              className="opacity-50"
                              style={{
                                fontSize: "0.75rem",
                                lineHeight: "1.2",
                              }}
                            >
                              SKU: {item.sku}
                            </small>
                            {item?.itemVendor ? (
                              <small
                                className="opacity-50"
                                style={{
                                  fontSize: "0.75rem",
                                  lineHeight: "1.2",
                                }}
                              >
                                Vendor: {item.itemVendor}
                              </small>
                            ) : null}
                            <small
                              className="opacity-50"
                              style={{
                                fontSize: "0.75rem",
                                lineHeight: "1.2",
                              }}
                            >
                              {item?.category}
                            </small>
                            <small
                              className="opacity-50"
                              style={{
                                fontSize: "0.75rem",
                                lineHeight: "1.2",
                              }}
                            >
                              {item?.subcategory}
                            </small>
                          </div>
                        </div>

                        {/* Location Info */}
                        <div
                          className="d-md-flex flex-md-row gap-2 align-items-center"
                          style={{ minWidth: "160px" }}
                        >
                          <LocationOnOutlinedIcon
                            style={{ fontSize: "1rem" }}
                            className="opacity-50"
                          />
                          <div className="d-flex flex-column text-start">
                            <small
                              style={{
                                fontSize: "0.85rem",
                                lineHeight: "1.2",
                                fontWeight: "bold",
                              }}
                            >
                              {transfer.toLocation}
                            </small>
                            <small
                              className="opacity-75"
                              style={{
                                fontSize: "0.75rem",
                                lineHeight: "1.2",
                              }}
                            >
                              {Object.keys(projectAddressMap).length > 0 ? (
                                projectAddressMap[transfer.toLocation] ||
                                "No address provided"
                              ) : (
                                <Skeleton.Input
                                  style={{ width: 80, height: 12 }}
                                  active
                                  size="small"
                                />
                              )}
                            </small>
                            <small
                              style={{
                                fontSize: "0.75rem",
                                lineHeight: "1.2",
                              }}
                            >
                              {transfer.toSubLocation}
                            </small>
                          </div>
                        </div>

                        {/* Quantity Info */}
                        <div
                          className="d-flex justify-content-end"
                          style={{ minWidth: "160px" }}
                        >
                          <div className="text-center text-md-end">
                            <small
                              className="fw-bold"
                              style={{
                                fontSize: "0.8rem",
                                lineHeight: "1",
                              }}
                            >
                              Quantity
                            </small>
                            <h5
                              style={{
                                color: "#1777FF",
                                fontWeight: "bold",
                                lineHeight: "1",
                                margin: 0,
                                fontSize: "1.1rem",
                              }}
                            >
                              {item.quantity}{" "}
                              {item.quantity > 1 ? "units" : "unit"}
                            </h5>
                          </div>
                        </div>

                        {/* Amount column */}
                        <div
                          className="d-flex flex-column text-end"
                          style={{ minWidth: "160px" }}
                        >
                          <small
                            className="fw-bold"
                            style={{
                              fontSize: "0.8rem",
                              lineHeight: "1",
                            }}
                          >
                            Amount
                          </small>
                          <h5
                            className=" opacity-50"
                            style={{
                              fontWeight: "bold",
                              lineHeight: "1",
                              margin: 0,
                              fontSize: "1.1rem",
                            }}
                          >
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: "USD",
                            }).format(item.unitPrice * item.quantity)}
                          </h5>
                        </div>
                      </div>

                      {/* Add hr except after the last item */}
                      {index < transfer.items.length - 1 && (
                        <hr className="my-2" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <hr className=" w-100" />
              <div className=" d-flex justify-content-between align-items-center w-100">
                <div className="">
                  <strong
                    className=" opacity-75"
                    style={{ fontSize: "0.85rem" }}
                  >
                    Notes
                  </strong>
                  <br />
                  <i style={{ fontSize: "0.8rem" }}>{transfer.note || ""}</i>
                </div>
                <div>
                  {transfer.approvalStatus === "approved" && (
                    <div className="d-flex align-items-center gap-1 text-success">
                      <CheckOutlined
                        className="opacity-75"
                        style={{ fontSize: "0.9rem" }}
                      />
                      <strong style={{ fontSize: "0.85rem" }}>Approved</strong>
                    </div>
                  )}
                  {transfer.approvalStatus === "rejected" && (
                    <div className="d-flex align-items-center gap-1 text-danger">
                      <Block
                        className="opacity-75"
                        style={{ fontSize: "0.9rem" }}
                      />
                      <strong style={{ fontSize: "0.85rem" }}>Rejected</strong>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}
      />
    </div>
  );

  const tabItems = [
    {
      key: "1",
      label: "Requested Transfers",
      children: pendingTransfersTab,
    },
    {
      key: "2",
      label: "History",
      children: historyTab,
    },
  ].filter(Boolean);

  return (
    <Modal
      open={requestedTransferModalVisible}
      onCancel={onCancel}
      footer={null}
      //   title="Items Shipped"
      width={900}
      centered
    >
      <div className="d-flex gap-3 flex-wrap mb-3">
        <div style={{ minWidth: "240px", flex: "1 1 240px" }}>
          <div
            className="mb-2"
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#667085",
            }}
          >
            From Location
          </div>
          <Select
            allowClear
            showSearch
            size="large"
            placeholder="All from locations"
            value={selectedFromLocationFilter}
            options={fromLocationOptions}
            onChange={setSelectedFromLocationFilter}
            style={{ width: "100%" }}
            className={styles.pillSelect}
            optionFilterProp="label"
          />
        </div>

        <div style={{ minWidth: "300px", flex: "1 1 300px" }}>
          <div
            className="mb-2"
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#667085",
            }}
          >
            To Location
          </div>
          <TreeSelect
            allowClear
            showSearch
            size="large"
            treeDefaultExpandAll
            placeholder="All to locations"
            value={selectedToLocationFilter}
            treeData={toLocationTreeData}
            onChange={setSelectedToLocationFilter}
            style={{ width: "100%" }}
            className={styles.pillSelect}
            treeNodeFilterProp="title"
          />
        </div>
      </div>

      <Tabs
        defaultActiveKey="1"
        items={tabItems}
        disabled={isRequestedTransfersLoading}
      />
    </Modal>
  );
}

export default RequestedTransfersModal;
